import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import { unwrapBlobError } from '@/Network/Requests/blobError'
import {
  NpsSummaryItem,
  NpsDoctorRankingItem,
  NpsHealthUnitRankingItem,
  NpsWordCloudItem,
  NpsCommentItem,
  NpsNegativeDriversAnalytics,
  NpsAnalyticsFilters,
  NpsFilterOption,
  ListNpsDoctorRankingFullResponse,
  ListNpsHealthUnitRankingFullResponse,
  NpsDoctorReportResponse
} from '@/Network/Types/Responses/NpsResponses'
import { NpsRankingFullFilters, NpsDoctorReportFilters } from '@/Network/Types/Requests/NpsRequests'

function buildQueryString<T extends object>(filters?: T): string {
  if (!filters) return ''
  const qs = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value == null) return
    if (Array.isArray(value)) {
      value.forEach((v) => qs.append(key, String(v)))
    } else {
      qs.set(key, String(value))
    }
  })
  const result = qs.toString()
  return result ? `?${result}` : ''
}

export default class NpsAnalyticsService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/nps/analytics'
  }

  async getSummary(filters?: NpsAnalyticsFilters): Promise<NpsSummaryItem[]> {
    try {
      const { data } = await request.get<NpsSummaryItem[]>(
        `${this.getCompletePath()}/summary${buildQueryString(filters)}`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar o resumo do NPS.')
    }
  }

  async getDoctorRanking(filters?: NpsAnalyticsFilters): Promise<NpsDoctorRankingItem[]> {
    try {
      const { data } = await request.get<NpsDoctorRankingItem[]>(
        `${this.getCompletePath()}/doctor-ranking${buildQueryString(filters)}`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar o ranking de médicos.')
    }
  }

  async getHealthUnitRanking(filters?: NpsAnalyticsFilters): Promise<NpsHealthUnitRankingItem[]> {
    try {
      const { data } = await request.get<NpsHealthUnitRankingItem[]>(
        `${this.getCompletePath()}/health-unit-ranking${buildQueryString(filters)}`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar o ranking de unidades.')
    }
  }

  async getWordCloud(filters?: NpsAnalyticsFilters): Promise<NpsWordCloudItem[]> {
    try {
      const { data } = await request.get<NpsWordCloudItem[]>(
        `${this.getCompletePath()}/word-cloud${buildQueryString(filters)}`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar a nuvem de palavras.')
    }
  }

  async getComments(filters?: NpsAnalyticsFilters): Promise<NpsCommentItem[]> {
    try {
      const { data } = await request.get<NpsCommentItem[]>(
        `${this.getCompletePath()}/comments${buildQueryString(filters)}`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os comentários.')
    }
  }

  /**
   * Filtros do dashboard escopados ao NPS (Unidade / Escala / Médico).
   *
   * Substituem os antigos endpoints admin de outros módulos (health-units/schedules), que
   * exigiam papéis admin fora do NPS e deixavam os dropdowns vazios para quem só tinha permissão
   * de NPS (Causa B da issue #1827). A falha lança `ServiceError`: o consumidor decide se
   * avisa (toast) e mantém o dropdown vazio.
   */
  async listFilterHealthUnits(): Promise<NpsFilterOption[]> {
    try {
      const { data } = await request.get<NpsFilterOption[]>(
        `${this.getCompletePath()}/filters/health-units`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as unidades do filtro.')
    }
  }

  async listFilterSchedules(): Promise<NpsFilterOption[]> {
    try {
      const { data } = await request.get<NpsFilterOption[]>(
        `${this.getCompletePath()}/filters/schedules`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as escalas do filtro.')
    }
  }

  async listFilterDoctors(): Promise<NpsFilterOption[]> {
    try {
      const { data } = await request.get<NpsFilterOption[]>(
        `${this.getCompletePath()}/filters/doctors`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os médicos do filtro.')
    }
  }

  async getNegativeDrivers(filters?: NpsAnalyticsFilters): Promise<NpsNegativeDriversAnalytics> {
    try {
      const { data } = await request.get<NpsNegativeDriversAnalytics>(
        `${this.getCompletePath()}/negative-drivers${buildQueryString(filters)}`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os motivadores negativos.')
    }
  }

  async exportReport(filters?: NpsAnalyticsFilters): Promise<HTMLAnchorElement> {
    try {
      const { data } = await request.get<BlobPart>(
        `${this.getCompletePath()}/export${buildQueryString(filters)}`,
        undefined,
        undefined,
        'blob'
      )
      const url = window.URL.createObjectURL(
        new Blob([data], {
          type: 'text/csv; charset=utf-8'
        })
      )
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'relatorio-nps.csv')
      return link
    } catch (e) {
      throw this.handleError(await unwrapBlobError(e), 'Não foi possível exportar o relatório.')
    }
  }

  async getDoctorRankingFull(
    filters?: NpsRankingFullFilters
  ): Promise<ListNpsDoctorRankingFullResponse> {
    try {
      const { data } = await request.get<ListNpsDoctorRankingFullResponse>(
        `${this.getCompletePath()}/doctor-ranking/full${buildQueryString(filters)}`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar o ranking de médicos.')
    }
  }

  async getHealthUnitRankingFull(
    filters?: NpsRankingFullFilters
  ): Promise<ListNpsHealthUnitRankingFullResponse> {
    try {
      const { data } = await request.get<ListNpsHealthUnitRankingFullResponse>(
        `${this.getCompletePath()}/health-unit-ranking/full${buildQueryString(filters)}`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar o ranking de unidades.')
    }
  }

  async getDoctorReport(
    doctorId: number,
    filters?: NpsDoctorReportFilters
  ): Promise<NpsDoctorReportResponse> {
    try {
      const { data } = await request.get<NpsDoctorReportResponse>(
        `${this.getCompletePath()}/doctor-report/${doctorId}${buildQueryString(filters)}`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar o relatório do médico.')
    }
  }

  async exportDoctorRanking(filters?: NpsRankingFullFilters): Promise<void> {
    try {
      const { data } = await request.get<BlobPart>(
        `${this.getCompletePath()}/doctor-ranking/export${buildQueryString(filters)}`,
        undefined,
        undefined,
        'blob'
      )
      const url = window.URL.createObjectURL(
        new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
      )
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'ranking-medicos.xlsx')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (e) {
      throw this.handleError(
        await unwrapBlobError(e),
        'Não foi possível exportar o ranking de médicos.'
      )
    }
  }

  async exportHealthUnitRanking(filters?: NpsRankingFullFilters): Promise<void> {
    try {
      const { data } = await request.get<BlobPart>(
        `${this.getCompletePath()}/health-unit-ranking/export${buildQueryString(filters)}`,
        undefined,
        undefined,
        'blob'
      )
      const url = window.URL.createObjectURL(
        new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
      )
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'ranking-unidades.xlsx')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (e) {
      throw this.handleError(
        await unwrapBlobError(e),
        'Não foi possível exportar o ranking de unidades.'
      )
    }
  }

  async exportDoctorReport(doctorId: number, filters?: NpsDoctorReportFilters): Promise<void> {
    try {
      const { data } = await request.get<BlobPart>(
        `${this.getCompletePath()}/doctor-report/${doctorId}/export${buildQueryString(filters)}`,
        undefined,
        undefined,
        'blob'
      )
      const url = window.URL.createObjectURL(
        new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
      )
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `relatorio-medico-${doctorId}.xlsx`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (e) {
      throw this.handleError(
        await unwrapBlobError(e),
        'Não foi possível exportar o relatório do médico.'
      )
    }
  }
}
